import { inject } from '@angular/core';
import { CanActivateChildFn, CanActivateFn, Router } from '@angular/router';
import { AuthService } from './auth.service';
import { map, of, catchError } from 'rxjs';

export const authGuard: CanActivateFn = (_route, state) => {
  const auth   = inject(AuthService);
  const router = inject(Router);

  if (auth.isLoggedIn()) return true;

  const sendToLogin = () => {
    auth.rememberReturnUrl(state.url);
    router.navigate(['/login']);
  };

  const token = auth.getAccessToken();
  if (!token) {
    sendToLogin();
    return false;
  }

  return auth.loadCurrentUser().pipe(
    map(() => {
      if (auth.isLoggedIn()) return true;
      sendToLogin();
      return false;
    }),
    catchError(() => {
      sendToLogin();
      return of(false);
    })
  );
};

export const adminGuard: CanActivateFn = () => {
  const auth   = inject(AuthService);
  const router = inject(Router);
  if (auth.isAdmin()) return true;
  router.navigate(['/dashboard']);
  return false;
};

// CRM Dodaj na końcu pliku guards.ts:
export const crmGuard: CanActivateFn = () => {
  const auth   = inject(AuthService);
  const router = inject(Router);
  const user   = auth.user();

  const hasCrmAccess = user?.is_admin ||
    user?.crm_role === 'salesperson' ||
    user?.crm_role === 'sales_manager';

  if (hasCrmAccess) return true;
  router.navigate(['/dashboard']);
  return false;
};

// Guard dla ekranu User Management — dostępny dla admina i Sales Managera
export const adminOrSalesManagerGuard: CanActivateFn = () => {
  const auth   = inject(AuthService);
  const router = inject(Router);
  const user   = auth.user();

  if (user?.is_admin || user?.crm_role === 'sales_manager') return true;
  router.navigate(['/dashboard']);
  return false;
};

export const superAdminGuard: CanActivateFn = () => {
  const auth   = inject(AuthService);
  const router = inject(Router);
  if (auth.isSuperAdmin()) return true;
  router.navigate(['/dashboard']);
  return false;
};

// Root ('') is the public marketing home for anonymous visitors (SSR-rendered for SEO),
// but a logged-in user landing on '/' should still go straight to their dashboard.
export const publicRootGuard: CanActivateFn = () => {
  const auth   = inject(AuthService);
  const router = inject(Router);
  if (auth.getAccessToken()) {
    router.navigate(['/dashboard']);
    return false;
  }
  return true;
};



const EXTERNAL_USER_ALLOWED_PATHS = ['/projects', '/my-settings'];

// An external account takes part in projects only; every other page of the
// shell sends it back to the project list. The backend enforces the same rule.
export const externalUserGuard: CanActivateChildFn = (_route, state) => {
  const auth   = inject(AuthService);
  const router = inject(Router);
  if (!auth.user()?.is_external) return true;
  const isAllowed = EXTERNAL_USER_ALLOWED_PATHS.some(
    path => state.url === path || state.url.startsWith(path + '/') || state.url.startsWith(path + '?'),
  );
  return isAllowed ? true : router.createUrlTree(['/projects']);
};
